import { formatDateToApiIso } from "@/src/utils/formatDateToApiIso";
import { formatOfficeAccountingGeneralStatusForApi } from "@/src/constants/officeAccountingTableConfig";
import {
  FORM_OF_EDUCATION_DISTANCE,
  FORM_OF_EDUCATION_FULL_TIME,
  normalizeFormOfEducationValue,
} from "@/src/constants/AdmissionOffice/Malta/maltaFormOfEducationOptions";

export type OfficeAccountingUpdateTarget = "accounting" | "applicant";

export interface OfficeAccountingColumnUpdateMapping {
  target: OfficeAccountingUpdateTarget;
  apiField: string;
  isDate?: boolean;
}

/** Columns that already have edit popups in Office Accounting table. */
const COLUMN_UPDATE_MAP: Record<string, OfficeAccountingColumnUpdateMapping> = {
  country: { target: "applicant", apiField: "country" },
  dateOfBirth: { target: "applicant", apiField: "birth_date", isDate: true },
  passportIssued: { target: "applicant", apiField: "passport_issued", isDate: true },
  validPassport: { target: "applicant", apiField: "valid_passport", isDate: true },
  phone: { target: "applicant", apiField: "phone" },
  email: { target: "applicant", apiField: "email" },
  preparatoryDepartment: { target: "applicant", apiField: "preparatory_department" },
  certificationEqualization: { target: "applicant", apiField: "certification_equalization" },

  // Former task fields — now on applicant-accounting
  admissionSemester: { target: "accounting", apiField: "admission_semester" },
  intake: { target: "accounting", apiField: "intake" },
  tuitionFee: { target: "accounting", apiField: "tuition_fee" },
  paymentForMissedClasses: { target: "accounting", apiField: "payment_for_missed_classes" },
  nostrification: { target: "accounting", apiField: "nostrification" },

  managersEmail: { target: "accounting", apiField: "managers_email" },
  agentsEmail: { target: "accounting", apiField: "agents_email" },
  fieldOfStudy: { target: "accounting", apiField: "field_of_study" },
  currentSemester: { target: "accounting", apiField: "current_semester" },
  generalStatus: { target: "accounting", apiField: "general_status" },
  invoiceNumber: { target: "accounting", apiField: "invoice_number" },
  statusAdmissionPayment: { target: "accounting", apiField: "status_admission_payment" },
  statusAl: { target: "accounting", apiField: "status_al" },
  statusEmail: { target: "accounting", apiField: "status_email" },
  statusEdbo: { target: "accounting", apiField: "status_edbo" },
  invitationFee: { target: "accounting", apiField: "invitation_fee" },
  statusInvitationFee: { target: "accounting", apiField: "status_invitation_fee" },
  balanceStatusUsd: { target: "accounting", apiField: "balance_status_usd" },
  statusNostrification: { target: "accounting", apiField: "status_nostrification" },
  statusMissedClasses: { target: "accounting", apiField: "status_missed_classes" },
  costFirstSemester: { target: "accounting", apiField: "cost_first_semester" },
  costSecondSemester: { target: "accounting", apiField: "cost_second_semester" },
  costThirdSemester: { target: "accounting", apiField: "cost_third_semester" },
  costFouthSemester: { target: "accounting", apiField: "cost_fourth_semester" },
  costFifthSemester: { target: "accounting", apiField: "cost_fifth_semester" },
  costSixthSemester: { target: "accounting", apiField: "cost_sixth_semester" },
  costSeventhSemester: { target: "accounting", apiField: "cost_seventh_semester" },
  costEighthSemester: { target: "accounting", apiField: "cost_eighth_semester" },
  costNinthSemester: { target: "accounting", apiField: "cost_ninth_semester" },
  costTenthSemester: { target: "accounting", apiField: "cost_tenth_semester" },
  costEleventhSemester: { target: "accounting", apiField: "cost_eleventh_semester" },
  costTwelfthSemester: { target: "accounting", apiField: "cost_twelfth_semester" },
};

export const mapOfficeAccountingColumnUpdate = (columnId: string): OfficeAccountingColumnUpdateMapping | null =>
  COLUMN_UPDATE_MAP[columnId] ?? null;

/** Date columns shown in Office Accounting as `DD.MM.YYYY`. */
export const OFFICE_ACCOUNTING_DATE_DISPLAY_COLUMNS = new Set([
  "dateOfBirth",
  "passportIssued",
  "validPassport",
  "dataAdmission",
  "validUntil",
  "opDData",
  "epDData",
  "omcData",
  "emcData",
  "dateAddEdbo",
  "ifDate",
  "ifDatePaid",
  "dataInvoice",
  "creditDateUsd",
  "creditDateEur",
]);

export const OFFICE_ACCOUNTING_INVITATION_FEE_ON_CAMPUS = "1500";
export const OFFICE_ACCOUNTING_INVITATION_FEE_ONLINE = "0";

/** Invitation Fee from Form of Education: On Campus → 1500, Online → 0. */
export const resolveInvitationFeeFromFormOfEducation = (formOfEducation?: unknown): string | null => {
  const normalized = normalizeFormOfEducationValue(formOfEducation);
  if (normalized === FORM_OF_EDUCATION_FULL_TIME) return OFFICE_ACCOUNTING_INVITATION_FEE_ON_CAMPUS;
  if (normalized === FORM_OF_EDUCATION_DISTANCE) return OFFICE_ACCOUNTING_INVITATION_FEE_ONLINE;
  return null;
};

/**
 * Table columns tied to applicant_info / Full Name popup overlap.
 * Confirm before changing these cells (even when there is no backend call yet).
 */
export const OFFICE_ACCOUNTING_APPLICANT_CONFIRM_COLUMNS = new Set([
  "email",
  "phone",
  "country",
  "dateOfBirth",
  "passportIssued",
  "validPassport",
  "preparatoryDepartment",
  "certificationEqualization",
  "levelEducation",
  "course",
  "formOfEducation",
]);

export const formatOfficeAccountingUpdateValue = (
  mapping: OfficeAccountingColumnUpdateMapping,
  value: string,
): string => {
  if (mapping.apiField === "general_status") {
    return formatOfficeAccountingGeneralStatusForApi(value);
  }
  return mapping.isDate ? formatDateToApiIso(value) || value.trim() : value;
};
